import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import {
  BookOpen, ChevronLeft, ChevronRight, Check, X, GraduationCap, Award,
  ClipboardCheck, AlertCircle, Trophy, Clock,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { academyApi } from './api';
import { academyInternalApi } from './apiInternal';
import AcademyInternalCertificate from './AcademyInternalCertificate';

/**
 * Lecteur de module interne SADA : leçons paginées + quiz + statut.
 * Props: trainingId, onClose
 */
const AcademyInternalPlayer = ({ trainingId, onClose, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [view, setView] = useState('overview'); // overview | lessons | quiz | result | cert
  const [lessonIndex, setLessonIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({}); // { qid: choiceId }
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [certData, setCertData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const full = await academyInternalApi.fullContent(trainingId);
      setData(full);
      setEnrollment(full.enrollment);
    } catch (e) {
      toast.error("Erreur de chargement du module.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [trainingId]);

  // Track si l'utilisateur a atteint la dernière leçon (persisté par navigateur)
  const readKey = `academy_lessons_read_${trainingId}`;
  const [hasReadAllLessons, setHasReadAllLessons] = useState(() => {
    try { return localStorage.getItem(readKey) === '1'; } catch (err) { return false; }
  });
  const markLessonsRead = () => {
    try { localStorage.setItem(readKey, '1'); } catch (err) { /* no-op */ }
    setHasReadAllLessons(true);
  };

  const bestAttempt = useMemo(() => {
    const attempts = enrollment?.quiz_attempts || [];
    if (attempts.length === 0) return null;
    return attempts.reduce((best, a) => (a.score_pct > (best?.score_pct || 0) ? a : best), null);
  }, [enrollment]);

  const attemptsUsed = enrollment?.quiz_attempts?.length || 0;
  const quizPassed = (enrollment?.quiz_attempts || []).some((a) => a.passed);
  const hasCertificate = !!enrollment?.internal_certificate;

  const startModule = async () => {
    try {
      const e = await academyApi.startEnrollment(trainingId);
      setEnrollment(e);
      setView('lessons');
      setLessonIndex(0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur.");
    }
  };

  const submitQuiz = async () => {
    if (!enrollment) return;
    const answers = Object.entries(quizAnswers).map(([qid, cid]) => ({ question_id: qid, choice_id: cid }));
    if (answers.length !== (data?.quiz?.questions?.length || 0)) {
      toast.error("Merci de répondre à toutes les questions.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await academyInternalApi.submitQuiz(enrollment.id, answers);
      setQuizResult(res);
      setView('result');
      // reload enrollment
      const full = await academyInternalApi.fullContent(trainingId);
      setEnrollment(full.enrollment);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de soumission.");
    } finally {
      setSubmitting(false);
    }
  };

  const openCertificate = async () => {
    try {
      const cd = await academyInternalApi.certificateData(enrollment.id);
      setCertData(cd);
      setView('cert');
    } catch (e) {
      toast.error("Certificat indisponible.");
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-slate-500">
        <BookOpen className="w-8 h-8 mx-auto animate-pulse" />
        Chargement du module…
      </div>
    );
  }
  if (!data) return null;

  const { training, lessons, quiz, practical } = data;

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-3" data-testid="academy-internal-player">
      <div className="w-full max-w-4xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* HEADER inline (pas de composant imbriqué) */}
        <div className="border-b border-slate-200 p-4 flex items-center justify-between gap-3 bg-emerald-50/40">
          <div className="min-w-0">
            <div className="text-xs text-emerald-700 font-medium">{training.code || 'Module interne SADA'}</div>
            <h2 className="text-lg font-bold text-slate-900 truncate">{training.title}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {quizPassed && !hasCertificate && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">En attente d&apos;éval. pratique</Badge>
            )}
            {hasCertificate && (
              <Badge className="bg-emerald-600 text-white">Certifié</Badge>
            )}
            <Button variant="outline" size="sm" onClick={onClose} data-testid="academy-player-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* ============== OVERVIEW ============== */}
          {view === 'overview' && (
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">Durée</div>
                  <div className="font-semibold text-slate-800">{training.duration}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">Points</div>
                  <div className="font-semibold text-slate-800">{training.points_mandatory} pts</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">Validité</div>
                  <div className="font-semibold text-slate-800">{training.validity_months} mois</div>
                </div>
              </div>
              <p className="text-sm text-slate-600">{training.description}</p>

              <div className="grid md:grid-cols-3 gap-3 pt-2">
                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1"><BookOpen className="w-4 h-4 text-blue-600" /><span className="font-semibold text-sm">Étape 1 · Leçons</span></div>
                  <div className="text-xs text-slate-500">{lessons.length} leçons courtes à parcourir</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1"><ClipboardCheck className="w-4 h-4 text-amber-600" /><span className="font-semibold text-sm">Étape 2 · Quiz</span></div>
                  <div className="text-xs text-slate-500">{quiz.questions.length} questions · seuil {quiz.passing_pct}% · {quiz.max_attempts} tentatives max</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1"><Trophy className="w-4 h-4 text-emerald-600" /><span className="font-semibold text-sm">Étape 3 · Pratique</span></div>
                  <div className="text-xs text-slate-500">Grille sur {practical.total_max} · seuil {practical.passing}/20 · validée par l&apos;admin</div>
                </CardContent></Card>
              </div>

              {enrollment && attemptsUsed > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <div className="font-medium text-blue-900">Votre progression</div>
                  <div className="text-blue-700 text-xs mt-1">
                    Tentatives quiz utilisées : {attemptsUsed}/{quiz.max_attempts}
                    {bestAttempt && <> · Meilleur score : <strong>{bestAttempt.score_pct}%</strong></>}
                    {quizPassed && !hasCertificate && <> · <strong>Quiz validé</strong>, en attente d&apos;évaluation pratique par l&apos;admin</>}
                    {hasCertificate && <> · <strong>Certifié !</strong></>}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                {!enrollment && (
                  <Button onClick={startModule} className="bg-emerald-600 hover:bg-emerald-700" data-testid="academy-internal-start-btn">
                    <BookOpen className="w-4 h-4 mr-2" /> Démarrer le module
                  </Button>
                )}
                {enrollment && (
                  <>
                    {/* Bouton PRINCIPAL : commencer / reprendre les leçons */}
                    <Button
                      onClick={() => { setView('lessons'); setLessonIndex(0); }}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="academy-internal-lessons-btn"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      {attemptsUsed > 0 || hasReadAllLessons ? 'Revoir les leçons' : 'Commencer les leçons'}
                    </Button>

                    {/* Bouton QUIZ : visible SEULEMENT après avoir lu toutes les leçons
                        (ou si l'utilisateur a déjà passé au moins une tentative). */}
                    {!quizPassed && attemptsUsed < quiz.max_attempts && (hasReadAllLessons || attemptsUsed > 0) && (
                      <Button
                        onClick={() => { setQuizAnswers({}); setView('quiz'); }}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="academy-internal-quiz-btn"
                      >
                        <ClipboardCheck className="w-4 h-4 mr-2" />
                        {attemptsUsed > 0 ? 'Réessayer le quiz' : 'Passer le quiz'} ({quiz.max_attempts - attemptsUsed} tent. restante{quiz.max_attempts - attemptsUsed > 1 ? 's' : ''})
                      </Button>
                    )}

                    {!quizPassed && attemptsUsed >= quiz.max_attempts && (
                      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> Tentatives épuisées — contactez l&apos;admin pour un déblocage.
                      </div>
                    )}
                    {hasCertificate && (
                      <Button onClick={openCertificate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="academy-internal-view-cert-btn">
                        <Award className="w-4 h-4 mr-2" /> Voir mon certificat
                      </Button>
                    )}
                  </>
                )}
              </div>
              {/* Message pédagogique (ligne dédiée, hors rangée de boutons) */}
              {enrollment && !quizPassed && attemptsUsed === 0 && !hasReadAllLessons && (
                <div className="text-xs text-blue-700 bg-blue-50 border-l-4 border-blue-400 rounded-r px-3 py-2 flex items-center gap-2 mt-1" data-testid="academy-lock-hint">
                  <BookOpen className="w-3.5 h-3.5 shrink-0" />
                  Lisez d&apos;abord toutes les leçons pour débloquer le quiz.
                </div>
              )}
            </div>
          )}

          {/* ============== LESSONS ============== */}
          {view === 'lessons' && (
            <div className="p-6 space-y-4">
              {/* Progression + retour aperçu */}
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setView('overview')} className="text-slate-600 hover:text-slate-900 -ml-2" data-testid="academy-lessons-back-to-overview">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Aperçu
                </Button>
                <div className="text-xs text-slate-500 whitespace-nowrap">Leçon {lessonIndex + 1}/{lessons.length}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${((lessonIndex + 1) / lessons.length) * 100}%` }} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-900">{lessons[lessonIndex]?.title}</h3>
              <div className="prose prose-sm max-w-none prose-slate prose-headings:font-semibold prose-table:text-sm">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{lessons[lessonIndex]?.content_md || ''}</ReactMarkdown>
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setLessonIndex(Math.max(0, lessonIndex - 1))} disabled={lessonIndex === 0}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Précédente
                </Button>
                {lessonIndex < lessons.length - 1 ? (
                  <Button
                    onClick={() => {
                      const next = lessonIndex + 1;
                      setLessonIndex(next);
                      if (next === lessons.length - 1) markLessonsRead();
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="academy-lesson-next"
                  >
                    Suivante <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => { markLessonsRead(); setQuizAnswers({}); setView('quiz'); }}
                    className="bg-amber-600 hover:bg-amber-700"
                    disabled={quizPassed || attemptsUsed >= quiz.max_attempts}
                    data-testid="academy-lesson-to-quiz"
                  >
                    Passer au quiz <ClipboardCheck className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* ============== QUIZ ============== */}
          {view === 'quiz' && (
            <div className="p-6 pb-28 space-y-5" data-testid="academy-quiz-view">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">Quiz de certification</h3>
                <div className="text-xs text-slate-500">
                  Tentative {attemptsUsed + 1}/{quiz.max_attempts} · Seuil {quiz.passing_pct}%
                </div>
              </div>
              {quiz.questions.map((q, i) => (
                <Card key={q.id} data-testid={`academy-quiz-q-${i + 1}`}>
                  <CardContent className="p-4">
                    <div className="font-medium text-slate-800 mb-2">Q{i + 1}. {q.question}</div>
                    <div className="space-y-1.5">
                      {q.choices.map((c) => (
                        <label
                          key={c.id}
                          className={`flex items-start gap-2 p-2 rounded cursor-pointer border transition-colors ${
                            quizAnswers[q.id] === c.id
                              ? 'bg-emerald-50 border-emerald-400'
                              : 'bg-white border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            value={c.id}
                            checked={quizAnswers[q.id] === c.id}
                            onChange={() => setQuizAnswers({ ...quizAnswers, [q.id]: c.id })}
                            className="mt-1"
                            data-testid={`academy-quiz-q${i + 1}-${c.id}`}
                          />
                          <span className="text-sm text-slate-700"><strong>{c.id}.</strong> {c.text}</span>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="flex justify-between pt-2 sticky bottom-0 bg-white py-3 border-t">
                <Button variant="outline" onClick={() => setView('lessons')}>Retour aux leçons</Button>
                <Button
                  onClick={submitQuiz}
                  disabled={submitting || Object.keys(quizAnswers).length !== quiz.questions.length}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="academy-quiz-submit"
                >
                  {submitting ? 'Envoi…' : `Soumettre le quiz (${Object.keys(quizAnswers).length}/${quiz.questions.length})`}
                </Button>
              </div>
            </div>
          )}

          {/* ============== RESULT ============== */}
          {view === 'result' && quizResult && (
            <div className="p-6 space-y-4" data-testid="academy-quiz-result">
              <div className={`text-center p-6 rounded-xl ${quizResult.attempt.passed ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${quizResult.attempt.passed ? 'bg-emerald-600' : 'bg-red-500'} text-white mb-3`}>
                  {quizResult.attempt.passed ? <Check className="w-8 h-8" /> : <X className="w-8 h-8" />}
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {quizResult.attempt.score_pct} %
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {quizResult.attempt.correct_count} / {quizResult.attempt.total} bonnes réponses
                </div>
                {quizResult.attempt.passed ? (
                  <div className="mt-3 text-emerald-700 font-medium">Quiz validé ! ✅</div>
                ) : (
                  <div className="mt-3 text-red-700 font-medium">
                    Non validé — il faut au moins {quiz.passing_pct} %.
                    {quizResult.attempts_left > 0
                      ? ` Vous pouvez retenter (${quizResult.attempts_left} restante${quizResult.attempts_left > 1 ? 's' : ''}).`
                      : ' Tentatives épuisées.'}
                  </div>
                )}
              </div>

              {/* Détail des réponses */}
              <div className="space-y-2">
                <div className="font-medium text-slate-800">Détail des réponses</div>
                {quizResult.attempt.details.map((d, i) => (
                  <div key={d.question_id} className={`p-3 rounded-lg border text-sm ${d.is_correct ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="font-medium">Q{i + 1}. {d.question}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Votre réponse : <strong>{d.given || '—'}</strong>
                      {!d.is_correct && <> · Bonne réponse : <strong>{d.correct_id}</strong></>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2 justify-end">
                {quizResult.attempt.passed ? (
                  <Button onClick={() => setView('overview')} className="bg-emerald-600 hover:bg-emerald-700">
                    Retour à l&apos;aperçu
                  </Button>
                ) : quizResult.attempts_left > 0 ? (
                  <>
                    <Button variant="outline" onClick={() => setView('lessons')}>Revoir les leçons</Button>
                    <Button
                      onClick={() => { setQuizAnswers({}); setQuizResult(null); setView('quiz'); }}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="academy-quiz-retry"
                    >
                      Réessayer
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setView('overview')} variant="outline">Retour</Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {view === 'cert' && certData && (
        <AcademyInternalCertificate data={certData} onClose={() => setView('overview')} />
      )}
    </div>
  );
};

export default AcademyInternalPlayer;
